import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, CalendarX2, Clock, Phone, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router";
import { publicApi } from "@/api/client";
import type { Slot } from "@/api/contract";
import { TimePicker } from "@/components/TimePicker";
import { Alert, Button, Card, Container, Eyebrow, LinkButton, PoleBar, Spinner } from "@/components/ui";
import { site } from "@/content";
import { downloadIcs, icsFor } from "@/lib/ics";
import { duration, money, whenLabel } from "@/lib/format";

const statusText: Record<string, string> = {
  booked: "Confirmed",
  checked_in: "Checked in",
  completed: "Completed",
  no_show: "Missed",
  cancelled: "Cancelled",
};

export function Manage() {
  const { token = "" } = useParams();
  const qc = useQueryClient();
  const [mode, setMode] = useState<"view" | "reschedule">("view");
  const [slot, setSlot] = useState<Slot>();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [notice, setNotice] = useState<string>();

  const q = useQuery({ queryKey: ["manage", token], queryFn: () => publicApi.managed(token), retry: false });

  const cancel = useMutation({
    mutationFn: () => publicApi.cancel(token),
    onSuccess: () => {
      setConfirmCancel(false);
      setNotice("Your appointment is cancelled. We hope to see you another time.");
      qc.invalidateQueries({ queryKey: ["manage", token] });
    },
  });

  const reschedule = useMutation({
    mutationFn: (s: Slot) => publicApi.reschedule(token, s.start),
    onSuccess: () => {
      setMode("view");
      setSlot(undefined);
      setNotice("Done! Your appointment has been moved. We've texted the new time.");
      qc.invalidateQueries({ queryKey: ["manage", token] });
    },
  });

  if (q.isLoading) return <Spinner label="Finding your booking" />;
  if (q.isError || !q.data)
    return (
      <Container className="max-w-xl py-16">
        <Alert tone="error">{(q.error as Error)?.message ?? "We couldn't find that booking."}</Alert>
        <p className="mt-6 text-muted">
          Call us at <a href={`tel:${site.phone}`} className="font-semibold text-rust">{site.phoneDisplay}</a> and we'll sort it out.
        </p>
      </Container>
    );

  const { appointment: a, canChange, cancellationWindowHours } = q.data;
  const cancelled = a.status === "cancelled";

  return (
    <Container className="max-w-2xl py-12">
      <Card className="overflow-hidden">
        <PoleBar />
        <div className="p-6 sm:p-10">
          <div className="flex items-center justify-between gap-4">
            <Eyebrow>Your appointment</Eyebrow>
            <span className={cancelled ? "rounded-full bg-sand px-3 py-1 text-xs font-semibold text-muted" : "rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800"}>
              {statusText[a.status]}
            </span>
          </div>
          <h1 className={`mt-4 font-display text-4xl ${cancelled ? "text-steel line-through" : ""}`}>{a.serviceName}</h1>
          <p className="mt-1 text-muted">with {a.stylistName}</p>
          <p className="mt-6 text-xl font-semibold">{whenLabel(a.start)}</p>
          <p className="mt-2 flex items-center gap-3 text-sm text-muted">
            <span className="inline-flex items-center gap-1">
              <Clock className="size-4" aria-hidden /> {duration(a.durationMinutes)}
            </span>
            <span>{money(a.priceCents, a.priceFrom)}</span>
            <span>
              {site.address.line1}, {site.address.city}
            </span>
          </p>

          {notice && (
            <div className="mt-6">
              <Alert tone="success">{notice}</Alert>
            </div>
          )}
          {(cancel.error || reschedule.error) && (
            <div className="mt-6">
              <Alert tone="error">{(cancel.error ?? reschedule.error)!.message}</Alert>
            </div>
          )}

          {mode === "view" && !cancelled && (
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <Button
                variant="secondary"
                onClick={() =>
                  downloadIcs(
                    "style-and-curl.ics",
                    icsFor({
                      id: a.id,
                      title: `${a.serviceName} at Style & Curl`,
                      start: a.start,
                      end: a.end,
                      location: `Style & Curl, ${site.address.line1}, ${site.address.city}, ${site.address.state} ${site.address.zip}`,
                      description: `With ${a.stylistName}. Manage: ${window.location.href}`,
                    }),
                  )
                }
              >
                <CalendarPlus className="size-4" aria-hidden /> Calendar
              </Button>
              <Button variant="secondary" disabled={!canChange} onClick={() => setMode("reschedule")}>
                <RefreshCw className="size-4" aria-hidden /> Reschedule
              </Button>
              <Button variant="danger" disabled={!canChange} onClick={() => setConfirmCancel(true)}>
                <CalendarX2 className="size-4" aria-hidden /> Cancel
              </Button>
            </div>
          )}

          {!canChange && !cancelled && a.status === "booked" && (
            <p className="mt-4 text-sm text-muted">
              Online changes close {cancellationWindowHours} hours before your appointment. Please call{" "}
              <a href={`tel:${site.phone}`} className="font-semibold text-rust">{site.phoneDisplay}</a>.
            </p>
          )}

          {mode === "reschedule" && (
            <div className="mt-8">
              <h2 className="font-display text-2xl">Pick a new time</h2>
              <div className="mt-6">
                <TimePicker
                  fetcher={publicApi.availability}
                  scope="manage"
                  serviceId={a.serviceId}
                  stylistId={a.stylistId}
                  exclude={token}
                  selected={slot}
                  onSelect={setSlot}
                />
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button disabled={!slot} loading={reschedule.isPending} onClick={() => slot && reschedule.mutate(slot)}>
                  {slot ? `Move to ${whenLabel(slot.start)}` : "Choose a time"}
                </Button>
                <Button variant="ghost" onClick={() => setMode("view")}>
                  Keep current time
                </Button>
              </div>
            </div>
          )}

          {cancelled && (
            <LinkButton to="/book" className="mt-8">
              Book a new appointment
            </LinkButton>
          )}

          <a href={`tel:${site.phone}`} className="mt-10 flex items-center gap-2 text-sm text-muted hover:text-rust">
            <Phone className="size-4" aria-hidden /> Questions? {site.phoneDisplay}
          </a>
        </div>
      </Card>

      <Dialog.Root open={confirmCancel} onOpenChange={setConfirmCancel}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm" />
          <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-[92%] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-card)] bg-white p-6 shadow-lift">
            <Dialog.Title className="font-display text-2xl">Cancel this appointment?</Dialog.Title>
            <Dialog.Description className="mt-2 text-muted">
              {a.serviceName} with {a.stylistName}, {whenLabel(a.start)}.
            </Dialog.Description>
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <Dialog.Close asChild>
                <Button variant="ghost">Keep it</Button>
              </Dialog.Close>
              <Button variant="primary" loading={cancel.isPending} onClick={() => cancel.mutate()}>
                Yes, cancel
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Container>
  );
}
